import type { MetaFunction, ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/cloudflare";
import { Form, useActionData, useNavigation, useLoaderData } from "@remix-run/react";
import { json } from "@remix-run/cloudflare";
import { useState, useCallback, useEffect } from "react";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Separator } from "~/components/ui/separator";
import { Turnstile } from "~/components/ui/turnstile";
import { verifyTurnstileToken, getClientIP } from "~/lib/turnstile.server";
import { 
  Mail, 
  MessageCircle, 
  Send, 
  MapPin, 
  Clock, 
  Shield,
  Terminal,
  Users,
  FileText,
  AlertCircle,
  CheckCircle
} from "lucide-react";

// Discord图标组件
function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0190 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9460 2.4189-2.1568 2.4189Z"/>
    </svg>
  );
}

// Action function to handle form submission
export async function action({ request, context }: ActionFunctionArgs) {
  const formData = await request.formData();
  const name = formData.get("name") as string;
  const email = formData.get("email") as string;
  const subject = formData.get("subject") as string;
  const message = formData.get("message") as string;
  const turnstileToken = formData.get("cf-turnstile-response") as string;

  // Basic validation
  if (!name || !email || !subject || !message) {
    return json({ 
      error: "All fields are required." 
    }, { status: 400 });
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return json({ 
      error: "Please enter a valid email address." 
    }, { status: 400 });
  }

  // Turnstile validation
  const env = (context as any)?.cloudflare?.env;
  const turnstileSecretKey = env?.TURNSTILE_SECRET_KEY;
  
  if (!turnstileSecretKey) {
    console.warn('⚠️ TURNSTILE_SECRET_KEY not configured - skipping verification');
  } else {
    if (!turnstileToken) {
      return json({ 
        error: "Please complete the security verification." 
      }, { status: 400 });
    }

    // Verify Turnstile token
    const clientIP = getClientIP(request);
    const turnstileResult = await verifyTurnstileToken({
      token: turnstileToken,
      secretKey: turnstileSecretKey,
      remoteip: clientIP,
    });

    if (!turnstileResult.success) {
      console.error('❌ Turnstile verification failed:', turnstileResult.error);
      return json({ 
        error: "Security verification failed. Please try again." 
      }, { status: 400 });
    }

    console.log('✅ Turnstile verification successful');
  }

  // 检查垃圾链接/网址特征
  const linkPattern = /((http|https):\/\/|www\.|\.(com|org|net|ru|cn|xyz|io)(\b|\/)|[a-zA-Z0-9\-]+\.(com|org|net|ru|cn|xyz|io)(\b|\/)|\/[a-zA-Z0-9\-]|\?|\.[a-zA-Z]{2,})/i;
  if (linkPattern.test(name) || linkPattern.test(message)) {
    return json({
      error: "Please do not include links, URLs, or suspicious content in the title or message. Your submission was blocked as a security measure."
    }, { status: 400 });
  }

  try {
    // Get environment variables
    const env = (context as any)?.cloudflare?.env;
    let resendApiKey = env?.RESEND_API_KEY;
    
    // If no environment key, use the provided key (for development)
    if (!resendApiKey) {
      resendApiKey = "re_6GvymyaK_6UMAzW3Nt1rr6zsF9XWVNwri";
    }

    // Create email content
    const emailContent = `
New contact form submission from Stellar Blade Mods website:

Name: ${name}
Email: ${email}
Subject: ${subject}

Message:
${message}

---
Sent from: stellarblademod.com/contact
Time: ${new Date().toISOString()}
User IP: ${request.headers.get('CF-Connecting-IP') || 'Unknown'}
    `.trim();

    // Send email using Resend API
    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${resendApiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        from: 'Stellar Blade Mods <contact@stellarblademod.com>',
        to: ['tuituijacob@gmail.com'],
        reply_to: email,
        subject: `[Stellar Blade Mods Contact] ${subject}`,
        text: emailContent,
        headers: {
          'Disposition-Notification-To': 'contact@stellarblademod.com',
          'Return-Receipt-To': 'contact@stellarblademod.com',
          'X-Confirm-Reading-To': 'contact@stellarblademod.com'
        },
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #0891b2;">New Contact Form Submission</h2>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <p><strong>Name:</strong> ${name}</p>
              <p><strong>Email:</strong> <a href="mailto:${email}">${email}</a></p>
              <p><strong>Subject:</strong> ${subject}</p>
            </div>
            <div style="background: #ffffff; padding: 20px; border: 1px solid #e2e8f0; border-radius: 8px;">
              <h3 style="margin-top: 0;">Message:</h3>
              <p style="white-space: pre-wrap;">${message}</p>
            </div>
            <hr style="margin: 30px 0; border: none; border-top: 1px solid #e2e8f0;">
            <p style="color: #64748b; font-size: 12px;">
              Sent from: stellarblademod.com/contact<br>
              Time: ${new Date().toLocaleString()}<br>
              User IP: ${request.headers.get('CF-Connecting-IP') || 'Unknown'}
            </p>
            <!-- Email tracking pixel -->
            <img src="https://stellarblademod.com/api/email-track?id=${Date.now()}&email=${encodeURIComponent(email)}" 
                 width="1" height="1" style="display:none;" alt="" />
          </div>
        `,
      }),
    });

    if (!response.ok) {
      const errorData = await response.text();
      console.error("Resend API error:", response.status, errorData);
      throw new Error(`Email service error: ${response.status}`);
    }

    const result = await response.json();
    console.log("Email sent successfully:", result);

    // Send auto-reply confirmation email to user
    try {
      const autoReplyResponse = await fetch('https://api.resend.com/emails', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${resendApiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from: 'Stellar Blade Mods <contact@stellarblademod.com>',
          to: [email],
          subject: `Message Received - ${subject}`,
          text: `
Hi ${name},

Thank you for contacting Stellar Blade Mods! We have successfully received your message regarding "${subject}".

Here's a copy of what you sent:
---
${message}
---

Our team will review your inquiry and get back to you within 24-48 hours. For faster support, consider joining our Discord community at https://discord.gg/daYR38qPB2

Best regards,
Stellar Blade Mods Team

---
This is an automated confirmation email. Please do not reply to this message.
If you need immediate assistance, please join our Discord community.
          `.trim(),
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #f8fafc; padding: 20px;">
              <div style="background: white; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
                <h2 style="color: #0891b2; margin-top: 0;">Message Received Successfully</h2>
                
                <p style="color: #374151; font-size: 16px;">Hi <strong>${name}</strong>,</p>
                
                <p style="color: #374151; line-height: 1.6;">
                  Thank you for contacting <strong>Stellar Blade Mods</strong>! We have successfully received your message regarding "<em>${subject}</em>".
                </p>
                
                <div style="background: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #0891b2;">
                  <h3 style="color: #374151; margin-top: 0; font-size: 14px; text-transform: uppercase; letter-spacing: 1px;">Your Message:</h3>
                  <p style="color: #6b7280; white-space: pre-wrap; margin: 0;">${message}</p>
                </div>
                
                <div style="background: #ecfdf5; padding: 20px; border-radius: 8px; margin: 20px 0; border: 1px solid #10b981;">
                  <h3 style="color: #059669; margin-top: 0; display: flex; align-items: center;">
                    <span style="margin-right: 8px;">✅</span> What happens next?
                  </h3>
                  <ul style="color: #047857; margin: 0; padding-left: 20px;">
                    <li>Our team will review your inquiry</li>
                    <li>We'll respond within <strong>24-48 hours</strong></li>
                    <li>You'll receive a detailed reply at this email address</li>
                  </ul>
                </div>
                
                <div style="background: #eff6ff; padding: 20px; border-radius: 8px; margin: 20px 0; border: 1px solid #3b82f6;">
                  <h3 style="color: #1d4ed8; margin-top: 0; display: flex; align-items: center;">
                    <span style="margin-right: 8px;">🚀</span> Need faster support?
                  </h3>
                  <p style="color: #1e40af; margin: 10px 0;">
                    Join our active Discord community for real-time help and discussions!
                  </p>
                  <a href="https://discord.gg/daYR38qPB2" 
                     style="display: inline-block; background: #5865f2; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
                    Join Discord Server
                  </a>
                </div>
                
                <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 30px 0;">
                
                <p style="color: #6b7280; font-size: 14px; margin: 0;">
                  Best regards,<br>
                  <strong>Stellar Blade Mods Team</strong>
                </p>
                
                <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #f3f4f6;">
                  <p style="color: #9ca3af; font-size: 12px; margin: 0;">
                    This is an automated confirmation email. Please do not reply to this message.<br>
                    If you need immediate assistance, please join our Discord community.
                  </p>
                </div>
              </div>
            </div>
          `,
        }),
      });

      if (autoReplyResponse.ok) {
        console.log("Auto-reply sent successfully");
      } else {
        console.error("Failed to send auto-reply:", await autoReplyResponse.text());
      }
    } catch (autoReplyError) {
      console.error("Error sending auto-reply:", autoReplyError);
      // Don't fail the main request if auto-reply fails
    }
    
    return json({ 
      success: true, 
      message: "Thank you for your message! We've received your inquiry and sent you a confirmation email. We'll get back to you within 24-48 hours." 
    });

  } catch (error) {
    console.error("Error sending email:", error);
    return json({ 
      error: "Sorry, there was an error sending your message. Please try again later or contact us directly via Discord for immediate assistance." 
    }, { status: 500 });
  }
}

// Loader function to pass environment variables to the client
export async function loader({ context }: LoaderFunctionArgs) {
  const env = (context as any)?.cloudflare?.env || {};
  
  return json({
    TURNSTILE_SITE_KEY: env.TURNSTILE_SITE_KEY || "1x00000000000000000000AA" // 测试密钥作为回退
  });
}

export const meta: MetaFunction = () => {
  return [
    { title: "Contact Us | Stellar Blade Mods" },
    { 
      name: "description", 
      content: "Get in touch with the Stellar Blade Mods team. Contact us for support, feedback, collaboration opportunities, or general inquiries about our mod community." 
    },
    { name: "keywords", content: "contact stellar blade mods, mod support, community feedback, collaboration, stellar blade help" },
    
    // Canonical URL
    { tagName: "link", rel: "canonical", href: "https://stellarblademod.com/contact" },
    
    // Open Graph
    { property: "og:title", content: "Contact Us | Stellar Blade Mods" },
    { property: "og:description", content: "Get in touch with the Stellar Blade Mods team for support, feedback, and collaboration opportunities." },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://stellarblademod.com/contact" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary" },
    { name: "twitter:title", content: "Contact Us | Stellar Blade Mods" },
    { name: "twitter:description", content: "Get in touch with the Stellar Blade Mods team for support, feedback, and collaboration opportunities." },
  ];
};

export default function Contact() {
  const actionData = useActionData<typeof action>();
  const loaderData = useLoaderData<typeof loader>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [turnstileError, setTurnstileError] = useState<string>("");
  
  // 从loader获取Turnstile site key
  const TURNSTILE_SITE_KEY = loaderData.TURNSTILE_SITE_KEY;
  
  // 使用useCallback优化回调函数，避免Turnstile组件重新渲染
  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
    setTurnstileError("");
  }, []);
  
  const handleTurnstileError = useCallback((error: string) => {
    setTurnstileError(error);
    setTurnstileToken("");
  }, []);
  
  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken("");
    setTurnstileError("Verification expired. Please try again.");
  }, []);
  
  // 表单提交成功后重置Turnstile
  const shouldResetTurnstile = actionData && 'success' in actionData && actionData.success;
  
  useEffect(() => {
    if (shouldResetTurnstile) {
      setTurnstileToken("");
      setTurnstileError("");
    }
  }, [shouldResetTurnstile]);
  
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <div className="relative text-white py-20 border-b border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <div className="rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 p-6 backdrop-blur-sm border border-white/10">
                <MessageCircle className="h-16 w-16 text-blue-400" />
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Contact Us
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Get in touch with our team for support, collaboration opportunities, or any questions about Stellar Blade mods.
            </p>
            
            <div className="flex items-center justify-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>Online Support</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>24-48 hour response</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Methods */}
          <div className="lg:col-span-1 space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">
                How to Reach Us
              </h2>
              
              <div className="space-y-6">
                {/* Discord */}
                <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                        <DiscordIcon className="h-6 w-6 text-indigo-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-200">Discord Community</h3>
                        <p className="text-sm text-slate-400">Real-time chat & support</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-300 mb-4">
                      Join our active Discord community for instant help, mod discussions, and direct communication with the team.
                    </p>
                    <Button asChild className="w-full bg-indigo-600 hover:bg-indigo-700">
                      <a 
                        href="https://discord.gg/daYR38qPB2" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        Join Discord Server
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                {/* Email */}
                <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                        <Mail className="h-6 w-6 text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-200">Email Support</h3>
                        <p className="text-sm text-slate-400">Formal inquiries & business</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-300 mb-4">
                      For detailed inquiries, business proposals, or formal support requests.
                    </p>
                    <div className="bg-slate-800/50 p-3 rounded-lg border text-sm text-blue-300 font-medium">
                      contact@stellarblademod.com
                    </div>
                  </CardContent>
                </Card>

                {/* Response Times */}
                <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <Clock className="h-6 w-6 text-green-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-200">Response Times</h3>
                        <p className="text-sm text-slate-400">Expected reply timeframes</p>
                      </div>
                    </div>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-300">Discord</span>
                        <span className="text-green-400">1-6 hours</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Contact Form</span>
                        <span className="text-yellow-400">24-48 hours</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Email</span>
                        <span className="text-blue-400">1-3 business days</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">
                  Send us a Message
                </CardTitle>
                <p className="text-slate-400">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Success/Error Messages */}
                {actionData && 'success' in actionData && actionData.success && (
                  <div className="bg-green-900/20 border-l-4 border-green-400 p-4 rounded">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-green-300 font-medium mb-1">Message Sent Successfully!</p>
                        <p className="text-sm text-slate-300">
                          {actionData.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {actionData && 'error' in actionData && actionData.error && (
                  <div className="bg-red-900/20 border-l-4 border-red-400 p-4 rounded">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-red-300 font-medium mb-1">Error</p>
                        <p className="text-sm text-slate-300">
                          {actionData.error}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contact Form */}
                <Form method="post" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                        Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        className="bg-slate-800/50 border-slate-600 text-slate-200 focus:border-cyan-400"
                        placeholder="Enter your name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                        Email *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="bg-slate-800/50 border-slate-600 text-slate-200 focus:border-cyan-400"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-slate-300 mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      className="w-full bg-slate-800/50 border border-slate-600 text-slate-200 rounded-md px-3 py-2 focus:border-cyan-400 focus:outline-none"
                    >
                      <option value="">Select a topic</option>
                      <option value="support">Technical Support</option>
                      <option value="mod-submission">Mod Submission</option>
                      <option value="collaboration">Collaboration Proposal</option>
                      <option value="feedback">Feedback & Suggestions</option>
                      <option value="bug-report">Bug Report</option>
                      <option value="business">Business Inquiry</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      className="w-full bg-slate-800/50 border border-slate-600 text-slate-200 rounded-md px-3 py-2 focus:border-cyan-400 focus:outline-none resize-y"
                      placeholder="Please provide detailed information about your inquiry..."
                    />
                  </div>
                  
                  <div className="bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-yellow-300 font-medium mb-1">Important Notice</p>
                        <p className="text-sm text-slate-300">
                          For faster support, consider joining our Discord community. 
                          This form is best for detailed inquiries and business matters.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Turnstile Security Verification */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Security Verification *
                    </label>
                    <div className="bg-slate-800/50 border border-slate-600 rounded-md p-4">
                      <Turnstile
                        siteKey={TURNSTILE_SITE_KEY}
                        onVerify={handleTurnstileVerify}
                        onError={handleTurnstileError}
                        onExpire={handleTurnstileExpire}
                        theme="dark"
                        size="normal"
                        className="flex justify-center"
                      />
                      {turnstileError && (
                        <div className="mt-2 text-sm text-red-400">
                          {turnstileError}
                        </div>
                      )}
                      <input
                        type="hidden"
                        name="cf-turnstile-response"
                        value={turnstileToken}
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isSubmitting || !turnstileToken}
                    className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </Form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
} 